import React, {useCallback, useEffect, useMemo, useRef} from 'react';

import {mapVideoProviderToProvider} from 'coreClasses/product/videoUtils';
import {Breakpoint} from 'coreClasses/view/IViewportMgr';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useLocation, useSetUI as useUIMgr, useUI} from 'coreHooks';

import Carousel, {ConfigType, ECarouselDirection, ECarouselPaginationType} from 'brandComponents/Carousel';
import Video from 'brandComponents/core/Video';
import Picture from 'brandComponents/Picture';
import {CONTROLS_OPTIONS_PDP, PRODUCT_IMAGES_SETTINGS, VIDEO_OPTIONS_PDP, ViewType} from 'brandConstants';

import {IImagesCarouselProps} from './IImagesCarousel';

import styles from './ImagesCarousel.module.scss';

const PDP_MAIN_VIEWTYPE = ViewType.ZOOM;
// The carousel is shown below `lg` (`ProductImages` from there).
const CAROUSEL_BREAKPOINTS: Breakpoint[] = ViewportMgr.getBreakpoints(true).slice(0, ViewportMgr.getBreakpoints(true).indexOf('lg'));

const ImagesCarousel = ({product}: IImagesCarouselProps) => {
	const UIMgr = useUIMgr();
	const isServerUI = useUI('isServer');
	const {imageIndex, isActive: isZoomActive} = useUI('pdpZoom');
	const productId = product.getProductId();
	const productVideos = useMemo(() => product.getVideos() || [], [productId]);
	const location = useLocation();
	const swiperRef = useRef(null);

	const carouselConfig: ConfigType = useMemo(() => ({
		loop: true,
		direction: ECarouselDirection.HORIZONTAL,
		slidesPerView: 1,
		initialSlide: 0,
		paginationConfig:{
			el: '.swiper-pagination-custom',
			type: ECarouselPaginationType.BULLETS,
			enabled: true,
			renderBullet: function(_, className) {
				return `<span class=" ${className} "></span>`;
			}
		},
		mousewheel: false,
		duplicateSlides: {
			enabled: false,
			slidesNumber: 0,
		},
		allowTouchMove: true
	}) as ConfigType, []);

	const slides = useMemo(() => {
		const imageSlides = (product.getProductImages(PDP_MAIN_VIEWTYPE, PRODUCT_IMAGES_SETTINGS.productCarousel) || [])
			.map((image, index) => (
				{
					key: image.getUrl(),
					elem: (
						<Picture
							image={image}
							index={index}
							priority={PRODUCT_IMAGES_SETTINGS.preloadImages.pdp && index === 0 && CAROUSEL_BREAKPOINTS}
							showPlaceholder={PRODUCT_IMAGES_SETTINGS.ShowPlaceholder.pdp}
							showSkeleton={!isServerUI}
							onClick={(e) => {
								UIMgr.setUI({
									pdpZoom: {
										isActive: true,
										imageIndex: index,
										clickedCoordinates: {
											x: e.nativeEvent.clientX,
											y: e.nativeEvent.clientY - e.target.getBoundingClientRect().top,
										}
									}
								});
							}}
						/>
					),
				}
			));

		productVideos.forEach((video) => {
			const index = video.position ?? imageSlides.length;

			imageSlides.splice(
				index,
				0,
				{
					key: 'video_' + index,
					elem: <div className={styles['images-carousel__video']}>
						<Video
							key={video.videoId}
							videoId={video.videoId}
							provider={mapVideoProviderToProvider(video.provider)}
							videoConfig={VIDEO_OPTIONS_PDP}
							controlsConfig={CONTROLS_OPTIONS_PDP}
							aspectRatio="unset"
						/>
					</div>
				}
			);
		});

		return imageSlides;
	}, [productId, isServerUI]);

	const onInitHandler = useCallback((swiper) => {
		swiperRef.current = swiper;
	}, []);

	useEffect(() => {
		UIMgr.setUI({
			pdpZoom: {
				imageIndex: 0,
			},
		});
	}, [location.pathname]);

	useEffect(() => {
		const getVideosTotalBeforeSlide = (slideIndex: number): number => {
			return productVideos.reduce((count, video) => {
				const videoPosition = video.position ?? slides.length - productVideos.length;

				return slideIndex >= videoPosition ? count + 1 : count;
			}, 0);
		};

		if (swiperRef.current && !isZoomActive) {
			swiperRef.current.slideToLoop(imageIndex + getVideosTotalBeforeSlide(imageIndex), 0);
		}
	}, [isZoomActive]);

	return (
		<div className={styles['images-carousel']}>
			<Carousel
				config={carouselConfig}
				handlers={{onInitHandler}}
				key={`imagesCarousel_${product.getProductId()}`}
			>
				{slides}
			</Carousel>
		</div>
	);
};

export default ImagesCarousel;
